import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

/**
 * Style picker shown as a dropdown instead of an inline list.
 * The menu is portalled to <body> and positioned with `position: fixed`, so it
 * floats above everything and is never clipped by the parent panel's
 * `overflow: hidden`, however tall the thumbnail grid gets.
 */
const StylePicker = ({ options, value, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const current = options.find((o) => o.key === value) || options[0];

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const menuW = 300;
    const menuH = 340;
    const gap = 6;

    // keep the menu inside the viewport horizontally
    let left = r.left;
    if (vw && left + menuW > vw - 8) {
      left = Math.max(8, vw - menuW - 8);
    }

    // default below the trigger; flip above only when there isn't room below
    // but there is above (guarded so a bad viewport reading can't break it)
    let top = r.bottom + gap;
    let bottom;
    if (vh && r.bottom + menuH > vh && r.top > vh - r.bottom) {
      top = undefined;
      bottom = vh - r.top + gap;
    }
    setPos({ top, bottom, left });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);

    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  const select = (key) => {
    onChange(key);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={open ? "dp-style-trigger open" : "dp-style-trigger"}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t("common.style")}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="dp-style-trigger-thumb">
          {current && <img src={current.src} alt={current.alt} />}
        </span>
        <svg className="dp-style-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="dp-style-menu"
            role="listbox"
            style={{
              top: pos.top,
              bottom: pos.bottom,
              left: pos.left,
            }}
          >
            {options.map(({ key, src, alt }) => (
              <button
                type="button"
                key={key}
                role="option"
                aria-selected={value === key}
                className={value === key ? "dp-style-thumb on" : "dp-style-thumb"}
                onClick={() => select(key)}
                title={alt}
              >
                <img src={src} alt={alt} />
                <span className="dp-style-check" aria-hidden="true">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m3 8 3.5 3.5L13 4.5" />
                  </svg>
                </span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

export default StylePicker;
